/**
 * API 服务 Hook
 * 提供网络请求、数据字典、文件上传等服务
 */
import { useCallback, useState, useEffect, useMemo } from "react";
import {
  getCoreConfig,
  request as coreRequest,
  getDictOptions as coreGetDictOptions,
  upload as coreUpload,
  getAccessToken as coreGetAccessToken,
  type RequestConfig,
  type UploadConfig,
  type DictOption,
} from "../../config";
import { extractApiResponseList } from "../utils/extractApiResponseList";

/**
 * 通用 API 服务 Hook
 */
export function useApiService() {
  const config = getCoreConfig();

  /**
   * 发起网络请求
   */
  const request = useCallback(
    async <T = any>(
      urlOrConfig: string | RequestConfig,
      options?: Omit<RequestConfig, "url">
    ): Promise<T> => {
      const requestConfig =
        typeof urlOrConfig === "string"
          ? { url: urlOrConfig, ...options }
          : urlOrConfig;
      return coreRequest(requestConfig);
    },
    []
  );

  /**
   * GET 请求
   */
  const get = useCallback(
    async <T = any>(
      url: string,
      params?: Record<string, any>,
      options?: Omit<RequestConfig, "url" | "params" | "method">
    ): Promise<T> => {
      return coreRequest({ url, params, method: "GET", ...options });
    },
    []
  );

  /**
   * POST 请求
   */
  const post = useCallback(
    async <T = any>(
      url: string,
      data?: any,
      options?: Omit<RequestConfig, "url" | "data" | "method">
    ): Promise<T> => {
      return coreRequest({ url, data, method: "POST", ...options });
    },
    []
  );

  /**
   * 获取数据字典
   */
  const getDictOptions = useCallback(
    async (dictCode: string): Promise<DictOption[]> => {
      return coreGetDictOptions(dictCode);
    },
    []
  );

  /**
   * 文件上传
   */
  const upload = useCallback(
    async (file: File, uploadConfig?: UploadConfig) => {
      return coreUpload(file, uploadConfig);
    },
    []
  );

  /**
   * 获取认证 Token
   */
  const getAccessToken = useCallback((): string | null => {
    return coreGetAccessToken();
  }, []);

  /**
   * 检查 API 是否已配置
   */
  const isConfigured = useCallback(() => {
    return !!config.request;
  }, [config.request]);

  return {
    request,
    get,
    post,
    getDictOptions,
    upload,
    getAccessToken,
    isConfigured,
    baseURL: config.baseURL,
  };
}

/**
 * 字典数据 Hook
 * 自动加载并缓存字典数据
 */
export function useDictOptions(dictCode: string | undefined) {
  const [options, setOptions] = useState<DictOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!dictCode) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    coreGetDictOptions(dictCode)
      .then((data) => {
        if (!cancelled) {
          setOptions(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          console.error(`Failed to load dict: ${dictCode}`, err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dictCode]);

  return { options, loading, error };
}

/**
 * 远程数据 Hook
 * 加载远程选项数据
 */
export function useRemoteOptions(
  url: string | undefined,
  config?: {
    labelKey?: string;
    valueKey?: string;
    immediate?: boolean;
    /** 请求时的查询参数，如 { pageSize: 100, status: 0 } */
    params?: Record<string, any>;
    /**
     * 响应中选项数组所在字段名。不传时：根为数组则直接用，否则取 list 或 records。
     * 指定后从根对象的该字段取数组，如 "children" 表示从 data.children 取选项列表。
     */
    field?: string;
  }
) {
  const [options, setOptions] = useState<DictOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const {
    labelKey = "label",
    valueKey = "value",
    immediate = true,
    field: responseField,
  } = config || {};

  // 稳定化 params 引用：按 JSON 值比较，避免调用方每次渲染传新对象导致无限重请求
  const paramsJson = JSON.stringify(config?.params ?? null);
  const stableParams = useMemo<Record<string, any> | undefined>(
    () => (config?.params != null ? JSON.parse(paramsJson) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramsJson]
  );

  const transformData = useCallback(
    (data: any[]): DictOption[] => {
      if (!Array.isArray(data)) return [];
      // 只取当前层的 label / value，截断 children，避免 Antd Select 把带 children 的项当 OptGroup
      return data.map((item) => ({
        label: item[labelKey],
        value: item[valueKey],
      }));
    },
    [labelKey, valueKey]
  );

  const load = useCallback(async () => {
    if (!url) return;

    setLoading(true);
    setError(null);

    try {
      const response = await coreRequest({
        url,
        method: "GET",
        ...(stableParams != null && Object.keys(stableParams).length > 0
          ? { params: stableParams }
          : {}),
      });
      const dataArray = extractApiResponseList(response, responseField);
      setOptions(transformData(dataArray as Record<string, unknown>[]));
    } catch (err) {
      setError(err as Error);
      console.error(`Failed to load remote options: ${url}`, err);
    } finally {
      setLoading(false);
    }
  }, [url, stableParams, transformData, responseField]);

  useEffect(() => {
    if (immediate && url) {
      load();
    }
  }, [immediate, url, load]);

  return { options, loading, error, reload: load };
}

export default useApiService;
